import Dexie from 'dexie';
import { db } from '../db.ts';
import { emptyFsrsState } from '../fsrsState.ts';
import { applyRating, DEFAULT_PARAMETERS } from '../../core/scheduler.ts';
import {
  nextLadderLevel,
  presentableLevel,
  type LadderDecision,
} from '../../core/ladder.ts';
import type {
  Card,
  Confidence,
  Grade,
  LadderLevel,
  ReviewLog,
  TargetLang,
  Timestamp,
} from '../types.ts';
import { flag } from '../types.ts';
import { startOfLocalDay } from '../../core/forecast.ts';
import { langOfItemId } from '../../core/coverage.ts';

/**
 * The single writer of FSRS state.
 *
 * SPEC §2.2 says no card advances without a recorded learner response. That is
 * enforced structurally here rather than by convention: `recordReview` cannot
 * be called without a rating, it always appends the log, and it does both
 * inside one transaction — so a card can never move without its log, and the
 * log can never be dropped to make a card look better.
 */

export const cardIdFor = (profileId: string, itemId: string): string =>
  `${profileId}::${itemId}`;

/** How many recent answers the promotion rule looks at (SPEC §2.3). */
const RECENT_WINDOW = 3;

export interface RecordReviewInput {
  profileId: string;
  itemId: string;
  grade: Grade;
  /** SPEC §2.12: asked before the reveal; never overrides the FSRS rating. */
  confidence: Confidence | null;
  latencyMs: number;
  answerRaw: string;
  correct: boolean;
  now: Timestamp;
  /**
   * Whether this item can be heard (SPEC §2.6). The *presented* rung is derived
   * from it here rather than by the caller, so the level in the log is always
   * the level the learner actually answered at.
   */
  audioAvailable?: boolean;
  /** SPEC §3.3: interference categories this answer matched. */
  interferenceHit?: string[];
}

export interface RecordReviewResult {
  card: Card;
  decision: LadderDecision;
  /** The rung the answer was given at, before any promotion or demotion. */
  answeredAt: LadderLevel;
}

/**
 * A card is created on the learner's first *answer*, not when the item is
 * queued — so an item they never responded to leaves no trace in the schedule.
 */
const loadOrCreateCard = async (
  profileId: string,
  itemId: string,
  now: Timestamp,
): Promise<Card> => {
  const id = cardIdFor(profileId, itemId);
  const existing = await db.cards.get(id);
  if (existing) return existing;

  const fsrs = emptyFsrsState(now);
  return { id, profileId, itemId, ladderLevel: 0, fsrs, dueAt: fsrs.dueAt, suspended: 0 };
};

export const recordReview = async (input: RecordReviewInput): Promise<RecordReviewResult> => {
  const card = await loadOrCreateCard(input.profileId, input.itemId, input.now);
  const audioAvailable = input.audioAvailable ?? false;
  // A dictation card on a silent device was presented one rung down, so that is
  // the rung the log has to record. Same rule the task builder applied.
  const answeredAt = presentableLevel(card.ladderLevel, audioAvailable);

  // SPEC §2.1's per-user parameter slot, set by the retune offer in SPEC §9.
  // Absent means the published defaults, which is what every learner starts on.
  const profile = await db.profiles.get(input.profileId);
  const scheduled = applyRating(
    card.fsrs,
    input.grade,
    input.now,
    profile?.requestRetention !== undefined
      ? {
          parameters: {
            ...DEFAULT_PARAMETERS,
            request_retention: profile.requestRetention,
          },
        }
      : undefined,
  );

  // Promotion looks at the last three answers *at this rung* (SPEC §2.3), which
  // is why ReviewLog carries the level it was answered at.
  const previous = await db.reviewLogs
    .where('cardId')
    .equals(card.id)
    .reverse()
    .sortBy('reviewedAt');
  const recentGrades = [
    input.grade,
    ...previous
      .filter((log) => log.ladderLevel === answeredAt)
      .slice(0, RECENT_WINDOW - 1)
      .map((log) => log.rating),
  ];

  const decision = nextLadderLevel({
    level: card.ladderLevel,
    grade: input.grade,
    stabilityDays: scheduled.state.stability,
    recentGrades,
    lapses: scheduled.state.lapses,
    audioAvailable,
  });

  const log: ReviewLog = {
    id: crypto.randomUUID(),
    profileId: input.profileId,
    cardId: card.id,
    ladderLevel: answeredAt,
    rating: input.grade,
    confidence: input.confidence,
    latencyMs: input.latencyMs,
    answerRaw: input.answerRaw,
    correct: flag(input.correct),
    // SPEC §3.3: the categories a wrong answer matched, so the heatmap is built
    // from what the learner actually did rather than from drill scores alone.
    ...(input.interferenceHit && input.interferenceHit.length > 0
      ? { interferenceHit: input.interferenceHit }
      : {}),
    // The item's first answer is the moment it joined the deck, and `previous`
    // has already told us (SPEC §7.2's daily cap counts these).
    ...(previous.length === 0 ? { introduction: flag(true) } : {}),
    reviewedAt: input.now,
    scheduledDays: scheduled.scheduledDays,
    elapsedDays: scheduled.elapsedDays,
    stateBefore: card.fsrs,
  };

  const updated: Card = {
    ...card,
    ladderLevel: decision.level,
    fsrs: scheduled.state,
    dueAt: scheduled.state.dueAt,
  };

  // One transaction: the log and the state move together or not at all.
  await db.transaction('rw', db.cards, db.reviewLogs, async () => {
    await db.cards.put(updated);
    await db.reviewLogs.add(log);
  });

  return { card: updated, decision, answeredAt };
};

/**
 * New words introduced since local midnight (SPEC §7.2).
 *
 * Reads the `[profileId+reviewedAt]` index rather than scanning: only today's
 * rows are fetched, and `introduction` is already on each of them. "Today" is
 * the device's own midnight, because a learner in Jakarta practising at 23:59
 * has not started tomorrow.
 */
export const introducedToday = async (
  profileId: string,
  now: Timestamp,
): Promise<number> => {
  const logs = await db.reviewLogs
    .where('[profileId+reviewedAt]')
    .between([profileId, startOfLocalDay(now)], [profileId, now], true, true)
    .toArray();
  return logs.filter((log) => log.introduction === 1).length;
};

export interface DueCardsOptions {
  /**
   * Only cards belonging to this language. A card carries no language of its
   * own — it is keyed by `profileId::itemId` — so the language is read off the
   * item id's namespace (`langOfItemId`).
   */
  lang?: TargetLang;
  limit?: number;
}

/**
 * Cards due for review, most at risk first is the composer's job — not this.
 *
 * The language filter runs **inside** the query, before the limit, and that
 * ordering is the whole point: filtering an already-capped page would let a
 * learner's Japanese backlog crowd every English card out of the first 200 rows
 * and leave their English session looking empty.
 */
export const dueCards = async (
  profileId: string,
  now: Timestamp,
  options: DueCardsOptions = {},
): Promise<Card[]> => {
  const { lang, limit = 200 } = options;
  const due = db.cards
    .where('[profileId+suspended+dueAt]')
    .between([profileId, 0, Dexie.minKey], [profileId, 0, now], true, true);

  return (lang === undefined ? due : due.filter((card) => langOfItemId(card.itemId) === lang))
    .limit(limit)
    .toArray();
};

/** How many are due, counted the same way the queue is built. */
export const dueCardCount = async (
  profileId: string,
  now: Timestamp,
  lang?: TargetLang,
): Promise<number> => {
  const due = db.cards
    .where('[profileId+suspended+dueAt]')
    .between([profileId, 0, Dexie.minKey], [profileId, 0, now], true, true);
  return lang === undefined
    ? due.count()
    : due.filter((card) => langOfItemId(card.itemId) === lang).count();
};
